/**
 * Diagnosis Context Provider
 * Manages global state for multi-modal dyslexia assessment
 */
import { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import {
  DiagnosisState,
  ChildAge,
  ReadingMetrics,
  WritingAnalysis,
  ChatbotAnalysis,
  BackendPrediction,
  CombinedExplanation,
  AssessmentStatus,
} from '@/types';
import { predictDyslexiaRisk } from '@/lib/api';

// ============================================================================
// Context Definition
// ============================================================================

interface DiagnosisContextType {
  state: DiagnosisState;
  setChildAge: (age: ChildAge) => void;
  setReadingData: (data: ReadingMetrics) => void;
  setWritingData: (data: WritingAnalysis) => void;
  setChatbotData: (data: ChatbotAnalysis) => void;
  processBackendPrediction: () => Promise<void>;
  calculateFinalScore: () => void;
  reset: () => void;
}

const DiagnosisContext = createContext<DiagnosisContextType | undefined>(
  undefined
);

// ============================================================================
// State & Actions
// ============================================================================

type DiagnosisAction =
  | { type: 'SET_CHILD_AGE'; payload: ChildAge }
  | { type: 'SET_READING_DATA'; payload: ReadingMetrics }
  | { type: 'SET_WRITING_DATA'; payload: WritingAnalysis }
  | { type: 'SET_CHATBOT_DATA'; payload: ChatbotAnalysis }
  | { type: 'SET_BACKEND_PREDICTION'; payload: BackendPrediction }
  | { type: 'SET_FINAL_SCORE'; payload: { score: number; classification: string; explanation: CombinedExplanation } }
  | { type: 'SET_STATUS'; payload: AssessmentStatus }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'INCREMENT_STEP' }
  | { type: 'RESET' };

const initialState: DiagnosisState = {
  child_age: null,
  reading_data: null,
  writing_data: null,
  chatbot_data: null,
  backend_prediction: null,
  final_score: null,
  final_classification: null,
  combined_explanation: null,
  status: 'idle',
  current_step: 0,
  total_steps: 3,
  error: null,
  reading_complete: false,
  writing_complete: false,
  chatbot_complete: false,
};

function diagnosisReducer(
  state: DiagnosisState,
  action: DiagnosisAction
): DiagnosisState {
  switch (action.type) {
    case 'SET_CHILD_AGE':
      return {
        ...state,
        child_age: action.payload,
      };

    case 'SET_READING_DATA':
      return {
        ...state,
        reading_data: action.payload,
        reading_complete: true,
        current_step: Math.max(state.current_step, 1),
      };

    case 'SET_WRITING_DATA':
      return {
        ...state,
        writing_data: action.payload,
        writing_complete: true,
        current_step: Math.max(state.current_step, 2),
      };

    case 'SET_CHATBOT_DATA':
      return {
        ...state,
        chatbot_data: action.payload,
        chatbot_complete: true,
        current_step: Math.max(state.current_step, 3),
      };

    case 'SET_BACKEND_PREDICTION':
      return {
        ...state,
        backend_prediction: action.payload,
      };

    case 'SET_FINAL_SCORE':
      return {
        ...state,
        final_score: action.payload.score,
        final_classification: action.payload.classification as DiagnosisState['final_classification'],
        combined_explanation: action.payload.explanation,
        status: 'complete',
      };

    case 'SET_STATUS':
      return {
        ...state,
        status: action.payload,
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        status: 'error',
      };

    case 'INCREMENT_STEP':
      return {
        ...state,
        current_step: Math.min(state.current_step + 1, state.total_steps),
      };

    case 'RESET':
      return initialState;

    default:
      return state;
  }
}

// ============================================================================
// Provider Component
// ============================================================================

interface DiagnosisProviderProps {
  children: ReactNode;
}

export function DiagnosisProvider({ children }: DiagnosisProviderProps) {
  const [state, dispatch] = useReducer(diagnosisReducer, initialState);

  const setChildAge = (age: ChildAge) => {
    dispatch({ type: 'SET_CHILD_AGE', payload: age });
  };

  const setReadingData = (data: ReadingMetrics) => {
    dispatch({ type: 'SET_READING_DATA', payload: data });
  };

  const setWritingData = (data: WritingAnalysis) => {
    dispatch({ type: 'SET_WRITING_DATA', payload: data });
  };

  const setChatbotData = (data: ChatbotAnalysis) => {
    dispatch({ type: 'SET_CHATBOT_DATA', payload: data });
  };

  const processBackendPrediction = async () => {
    if (!state.reading_data) {
      dispatch({ type: 'SET_ERROR', payload: 'No reading data available' });
      return;
    }

    try {
      dispatch({ type: 'SET_STATUS', payload: 'analyzing' });

      const prediction = await predictDyslexiaRisk({
        reading_data: {
          gaze_points: state.reading_data.gaze_points,
          text_length: state.reading_data.text_length,
          reading_duration: state.reading_data.reading_duration,
        },
      });

      dispatch({ type: 'SET_BACKEND_PREDICTION', payload: prediction });
    } catch (error) {
      console.error('Backend prediction failed:', error);
      dispatch({
        type: 'SET_ERROR',
        payload: 'Failed to get prediction from backend',
      });
    }
  };

  const calculateFinalScore = () => {
    const { backend_prediction, writing_data, chatbot_data } = state;

    console.log('=== Starting calculateFinalScore ===');
    console.log('backend_prediction:', backend_prediction);
    console.log('writing_data:', writing_data);
    console.log('chatbot_data:', chatbot_data);

    if (!backend_prediction) {
      console.error('No backend prediction available');
      dispatch({ type: 'SET_ERROR', payload: 'Backend prediction not available' });
      return;
    }

    try {
      // Weighted fusion algorithm
      const weights = {
        backend_ml: 0.45,
        gemini_writing: 0.25,
        gemini_chatbot: 0.20,
        eye_tracking: 0.10,
      };

      // Convert writing risk to numeric (0-100)
      const writingRiskNumeric = writing_data?.handwriting_pipeline?.final_score
        ?? writing_data?.gemini_structured?.overall_risk_score
        ?? (writing_data ? 30 : 50);

      // Convert chatbot risk to numeric (0-100)
      const chatbotRiskMap: { [key: string]: number } = { Low: 20, Moderate: 50, High: 80 };
      const chatbotRiskNumeric = chatbot_data
        ? chatbotRiskMap[chatbot_data.overall_cognitive_risk]
        : 50;

      // Eye tracking risk (based on regression index and fixation duration)
      const eyeTrackingRisk = state.reading_data
        ? calculateEyeTrackingRisk(state.reading_data)
        : 50;

      console.log('Risk components:', {
        backend_ml: backend_prediction.risk_score,
        writing: writingRiskNumeric,
        chatbot: chatbotRiskNumeric,
        eye_tracking: eyeTrackingRisk,
      });

      // Calculate weighted final score
      const finalScore =
        weights.backend_ml * backend_prediction.risk_score +
        weights.gemini_writing * writingRiskNumeric +
        weights.gemini_chatbot * chatbotRiskNumeric +
        weights.eye_tracking * eyeTrackingRisk;

      console.log('Final score calculated:', finalScore);

      // Classify based on final score
      let classification: 'Low Risk' | 'Moderate Risk' | 'High Risk';
      if (finalScore < 40) {
        classification = 'Low Risk';
      } else if (finalScore < 70) {
        classification = 'Moderate Risk';
      } else {
        classification = 'High Risk';
      }

      console.log('Classification:', classification);

      // Generate combined explanation
      const explanation = generateCombinedExplanation(
        backend_prediction,
        writing_data,
        chatbot_data
      );

      console.log('Explanation generated:', explanation);

      dispatch({
        type: 'SET_FINAL_SCORE',
        payload: {
          score: finalScore,
          classification,
          explanation,
        },
      });

      console.log('=== Final score dispatched successfully ===');
    } catch (error) {
      console.error('Error in calculateFinalScore:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to calculate final score' });
    }
  };

  const reset = () => {
    dispatch({ type: 'RESET' });
  };

  // Auto-calculate final score when backend prediction is received
  useEffect(() => {
    if (state.backend_prediction && !state.final_score) {
      console.log('Auto-calculating final score...');
      calculateFinalScore();
    }
  }, [state.backend_prediction]);

  const value: DiagnosisContextType = {
    state,
    setChildAge,
    setReadingData,
    setWritingData,
    setChatbotData,
    processBackendPrediction,
    calculateFinalScore,
    reset,
  };

  return (
    <DiagnosisContext.Provider value={value}>
      {children}
    </DiagnosisContext.Provider>
  );
}

// ============================================================================
// Hook
// ============================================================================

export function useDiagnosis(): DiagnosisContextType {
  const context = useContext(DiagnosisContext);
  if (!context) {
    throw new Error('useDiagnosis must be used within DiagnosisProvider');
  }
  return context;
}

// ============================================================================
// Helper Functions
// ============================================================================

function calculateEyeTrackingRisk(readingData: ReadingMetrics): number {
  // Higher regression index = higher risk
  const regressionRisk = (readingData.regression_index || 0) * 100;

  // Higher fixation duration = higher risk (typical: 200-250ms)
  const fixationRisk = readingData.avg_fixation_duration
    ? Math.min(((readingData.avg_fixation_duration - 250) / 250) * 100, 100)
    : 0;

  // Combine (simple average)
  return Math.max(0, Math.min((regressionRisk + fixationRisk) / 2, 100));
}

function generateCombinedExplanation(
  backendPrediction: BackendPrediction,
  writingData: WritingAnalysis | null,
  chatbotData: ChatbotAnalysis | null
): CombinedExplanation {
  const dynamicIndicatorKey = (indicator: string): string => {
    const entropyMatch = indicator.match(/entropy:\s*([0-9]+\.?[0-9]*)/i);
    if (entropyMatch) {
      return `results.eyeTracking.highVariability|entropy=${entropyMatch[1]}`;
    }

    const fixationMatch = indicator.match(/([0-9]+)\s*fixation/i);
    if (fixationMatch) {
      return `results.eyeTracking.excessiveFixations|count=${fixationMatch[1]}`;
    }

    const durationMatch = indicator.match(/(?:avg|average):?\s*([0-9]+\.?[0-9]*)\s*ms/i);
    if (durationMatch) {
      return `results.eyeTracking.longDurations|duration=${durationMatch[1]}`;
    }

    return indicator;
  };

  const normalizeIndicator = (indicator: string): string => {
    const map: Record<string, string> = {
      'Reading patterns within typical range': 'results.eyeTracking.normal',
      'Irregular temporal reading rhythm detected': 'results.eyeTracking.irregularRhythm',
      'Excessive fixation count observed': 'results.eyeTracking.excessiveFixations',
      'Prolonged fixation durations noted': 'results.eyeTracking.longDurations',
      'Excessive fixation': 'results.eyeTracking.excessiveFixations',
      'Prolonged fixation': 'results.eyeTracking.longDurations',
      'Letter reversal patterns noted in handwriting': 'results.handwriting.letterReversals',
      'Good cognitive engagement and comprehension': 'results.chatbot.description',
      'Below-average memory recall': 'results.indicators.behavioral.lowMemory',
      'Attention challenges observed': 'results.indicators.behavioral.attentionChallenges',
      'Comprehension difficulties noted': 'results.indicators.behavioral.comprehensionDifficulties',
    };

    return map[indicator] || dynamicIndicatorKey(indicator);
  };

  const primaryFactors: string[] = [];
  const readingIndicators: string[] = [];
  const writingIndicators: string[] = [];
  const behavioralIndicators: string[] = [];

  // Reading indicators
  if (backendPrediction.explanation.primary_indicators.length > 0) {
    const normalized = backendPrediction.explanation.primary_indicators.map(normalizeIndicator);
    primaryFactors.push(...normalized);
    readingIndicators.push(...normalized);
  }

  // Writing indicators
  const writingFlagMap: Record<string, string> = {
    HIGH_REVERSAL_FREQUENCY: 'results.handwriting.letterReversals',
    IRREGULAR_SPACING: 'results.handwriting.irregularSpacing',
    MOTOR_CONTROL_CONCERN: 'results.handwriting.tremor',
    ORIENTATION_INCONSISTENT: 'results.handwriting.combined',
  };

  if (writingData?.handwriting_pipeline?.clinical_flags?.length) {
    for (const flag of writingData.handwriting_pipeline.clinical_flags) {
      const indicator = writingFlagMap[flag];
      if (indicator) {
        writingIndicators.push(indicator);
      }
    }

    if (writingData.handwriting_pipeline.final_score >= 45) {
      primaryFactors.push('results.handwriting.combined');
    }
  }

  // Backward compatibility for string response extraction
  if (writingData && writingData.gemini_response) {
    const response = writingData.gemini_response.toLowerCase();
    
    if (response.includes('reversal') || response.includes('mirror')) {
      const indicator = 'results.handwriting.letterReversals';
      primaryFactors.push(indicator);
      writingIndicators.push(indicator);
    }
    
    if (response.includes('spacing') || response.includes('inconsistent')) {
      const indicator = 'results.handwriting.irregularSpacing';
      writingIndicators.push(indicator);
    }
    
    if (response.includes('tremor') || response.includes('shaky')) {
      const indicator = 'results.handwriting.tremor';
      writingIndicators.push(indicator);
    }
    
  }

  // If no specific issues found, add general indicator
  if (writingData && writingIndicators.length === 0) {
    writingIndicators.push('results.handwriting.normal');
  }

  // Behavioral indicators
  if (chatbotData) {
    if (chatbotData.memory_score < 5) {
      const indicator = 'results.indicators.behavioral.lowMemory';
      primaryFactors.push(indicator);
      behavioralIndicators.push(indicator);
    }
    if (chatbotData.attention_score < 5) {
      const indicator = 'results.indicators.behavioral.attentionChallenges';
      behavioralIndicators.push(indicator);
    }
    if (chatbotData.comprehension_score < 5) {
      const indicator = 'results.indicators.behavioral.comprehensionDifficulties';
      behavioralIndicators.push(indicator);
    }
    
    // If all scores are good, add positive indicator
    if (behavioralIndicators.length === 0) {
      behavioralIndicators.push('results.chatbot.description');
    }
  }

  // Calculate overall confidence
  const confidence = backendPrediction.confidence;

  // Generate recommendation based on combined data
  const recommendation = backendPrediction.explanation.recommendation;

  return {
    primary_factors: primaryFactors.slice(0, 5), // Top 5
    confidence,
    recommendation,
    detailed_breakdown: {
      reading: readingIndicators,
      writing: writingIndicators,
      behavioral: behavioralIndicators,
    },
  };
}